import {DetailPage} from "../../elements/Pages";
import React from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Link, useParams} from "react-router-dom";
import {getBook, unassignBookCode} from "../../../api/books";
import ListViewTable from "../../elements/tables/ListViewTable";
import {EditButton, PrimaryButton} from "../../elements/Buttons";
import {useAuth} from "../../../contexts/AuthContext";
import checkPermission from "../../../utils/permissions";


export default function BookDetail() {
    const {id} = useParams();
    const {permissions} = useAuth();
    const queryClient = useQueryClient()

    const {data: book} = useQuery(['books', id], () => getBook(id));

    if (!book) return <></>;

    const canEditBook = checkPermission(permissions, "books.change_book");
    const canViewBooks = checkPermission(permissions, "books.view_book");

    const columns = [
        {
            accessorKey: 'code',
            id: 'code',
            header: 'Code',
            enableColumnFilter: false,
        },
        {
            accessorFn: (row) => !!row.student,
            cell: ({row, value}) => (
                <>{row.original.student ? 'Ja' : 'Nein'}</>
            ),
            id: 'assigned',
            header: 'Vergeben',
            columnType: 'yesno',
            yesLabel: 'Ja',
            noLabel: 'Nein',
            enableColumnFilter: true,
        },
        {
            accessorFn: row => row.student ? row.student.full_name : '',
            id: 'student',
            header: 'SchülerIn',
            enableColumnFilter: false,
        }
    ];

    return (

        <DetailPage>
            <div
                className="max-w-3xl mx-auto px-4 sm:px-6 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl lg:px-8">
                <div className="flex items-center space-x-5">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{book.title}</h1>
                    </div>
                </div>
                <div
                    className="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3 md:mt-0 md:flex-row md:space-x-3">
                    {canEditBook && <PrimaryButton label="Codes importieren" link={`/books/${book.id}/import-codes`}/>}
                    {canEditBook && <EditButton label="Bearbeiten" link={`/books/${book.id}/edit`}/>}
                </div>
            </div>

            <div
                className="mt-8 max-w-3xl mx-auto grid grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
                <div className="space-y-6 lg:col-start-1 lg:col-span-3">

                    <section aria-labelledby="applicant-information-title">
                        <div className="bg-white shadow overflow-hidden sm:rounded-lg">

                            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
                                <dl className="sm:divide-y sm:divide-gray-200">

                                    <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                        <dt className="text-sm font-medium text-gray-500">Verlag</dt>
                                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{book.publisher.name}</dd>
                                    </div>
                                    <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                        <dt className="text-sm font-medium text-gray-500">Bestellnr.</dt>
                                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{book.order_number}</dd>
                                    </div>
                                    <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                        <dt className="text-sm font-medium text-gray-500">Format</dt>
                                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                            {book.is_digital ? 'Digital' : 'Druck'}
                                        </dd>
                                    </div>
                                    {canViewBooks && book.is_digital && <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                        <dt className="text-sm font-medium text-gray-500">Codes</dt>
                                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                            {book.available_codes} verfügbar, <span className={book.missing_code_count > 0 ? 'text-red-600 font-medium' : ''}>{book.missing_code_count} fehlend</span>
                                        </dd>
                                    </div>}

                                    <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                        <dt className="text-sm font-medium text-gray-500">Kurse</dt>
                                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                            {book.courses.map((course, index) => (
                                                <Link className="font-medium text-imsblue-600"
                                                      to={`/courses/${course.id}`} key={index}>{index > 0 ? ", " : ""}
                                                    {course.course_number}
                                                </Link>
                                            ))}

                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </section>

                    {canViewBooks && book.book_code_summary.students_missing_book && book.book_code_summary.students_missing_book.length > 0 && <div className="bg-yellow-50 shadow sm:rounded-lg">
                        <div className="px-4 py-5 sm:p-6">

                            <div className="grid grid-cols-1 gap-6">
                                {/* Students missing codes */}
                                { (
                                    <div>
                                        <h4 className="text-md font-mediumtext-gray-900 mb-3">
                                            SchülerInnen mit fehlenden Codes ({book.book_code_summary.students_missing_book.length})
                                        </h4>
                                        <div className="max-h-64 overflow-y-auto space-y-2">
                                            {book.book_code_summary.students_missing_book.map((student, index) => (
                                                <span key={index}><Link
                                                    to={`/school/students/${student.id}`}
                                                    className=" text-imsblue-600 hover:text-imsblue-900"
                                                >
                                                            {student.name} ({student.student_number})
                                                        </Link>, </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>}

                    <div className="space-y-6 lg:col-start-1 lg:col-span-3">
                        <ListViewTable
                            data={book?.codes}
                            columns={columns}
                            isLoading={!book}
                            disableGlobalSearch={false}
                        />
                    </div>

                </div>

            </div>

        </DetailPage>

    );
}
